import { buildNavGroups } from '../lib/nav-groups.js';
import { TabMounts } from './TabMounts.jsx';
import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { periodColor } from '../lib/ui.js'; import { todayStr } from '../lib/format.js';
import { DisputeAlertsModal } from './DisputeAlertsModal.jsx';
import { NotificationBell } from './NotificationBell.jsx';
import { TaskCard } from './TaskCard.jsx';
import { YesterdayView } from './YesterdayView.jsx';
import { MaintenanceView } from './MaintenanceView.jsx';
import { MissedTasksView } from './MissedTasksView.jsx';
import { EquipmentView } from './EquipmentView.jsx';
import { TemperaturesOpsView } from './TemperaturesOpsView.jsx';
import { ComplianceView } from './ComplianceView.jsx';
import { AdminView } from './AdminView.jsx';
import { LocationConfigView } from './LocationConfigView.jsx';
import { OverrideAuditView } from './OverrideAuditView.jsx';
import { AdminTogglesView } from './AdminTogglesView.jsx';
import { HolidayListView } from './HolidayListView.jsx';
import { PayrollRosterView } from './PayrollRosterView.jsx';
import { HiringSettingsView } from './HiringSettingsView.jsx';
import { EventsSettingsView } from './EventsSettingsView.jsx';
import { FixedCostsView } from './FixedCostsView.jsx';
import { LeaseDatesView } from './LeaseDatesView.jsx';
import { PnlReportView } from './PnlReportView.jsx';
import { FinancialHealthView } from './FinancialHealthView.jsx';
import { DailyCashCountEntry } from './DailyCashCountEntry.jsx';
import { MorningParPanel } from './MorningParPanel.jsx';
import { InventoryLogCard } from './InventoryLogCard.jsx';
import { TodayWeather } from './TodayWeather.jsx';
import { timeclockEnabled } from '../lib/flags.js';
import { TeamDirectoryView } from './TeamDirectoryView.jsx';
import { HiringApplicationsView } from './HiringApplicationsView.jsx';
import { EmployeeProfileView } from './EmployeeProfileView.jsx';
import { HomeLanding } from './HomeLanding.jsx';
import { OwnerDashboard } from './OwnerDashboard.jsx';
import { PunchBanner } from './PunchBanner.jsx';
import { RealtimeDashboard } from './RealtimeDashboard.jsx';
import { Step3View } from './Step3View.jsx';
import { Step1View } from './Step1View.jsx';
import { KbLibraryView } from './KbLibraryView.jsx';
import { AskEmpireView } from './AskEmpireView.jsx';
import { KbGapReportView } from './KbGapReportView.jsx';
import { RecipeCardsView } from './RecipeCardsView.jsx';
import { ColdBrewDilutionView } from './ColdBrewDilutionView.jsx';
import { ScheduleSection } from './ScheduleSection.jsx';
import { TaskManagerModal } from './TaskManagerModal.jsx';
import { SuggestTaskModal } from './SuggestTaskModal.jsx';
import { TaskBulkImportModal } from './TaskBulkImportModal.jsx';
import { KnowledgeDripCard } from './KnowledgeDripCard.jsx';
import { TallyLaunchView } from './TallyLaunchView.jsx';
import { RoastCurveLaunchView } from './RoastCurveLaunchView.jsx';
import { WelcomeTour } from './WelcomeTour.jsx';
import { resolveTabAlias } from '../lib/tab-aliases.js';

// Time-period → hours label, shown in the period-coverage strip (used only here).
const PERIOD_TIMES = {
  Opening: '5:00–6:00 AM', Morning: '6:00–10:00 AM',
  Midday: '10:00 AM–2:00 PM', Afternoon: '2:00–4:00 PM', Closing: '4:00–6:00 PM',
};

// Nav icons are normally emoji strings; an icon that looks like an image path
// (the Tally logo) renders as an <img> sized to match the surrounding emoji.
// The dark sidebar/bottom-bar use the white mark; onLight surfaces (the mobile
// "More" drawer, which is white) swap to the crimson "-red" variant so it's
// visible.
function navIcon(icon, onLight = false) {
  if (typeof icon === 'string' && (icon.startsWith('/') || icon.endsWith('.png') || icon.endsWith('.svg'))) {
    const src = onLight ? icon.replace(/\.png$/, '-red.png') : icon;
    return <img src={src} alt="" style={{ width: '1.15em', height: '1.15em', objectFit: 'contain', verticalAlign: 'middle' }} />;
  }
  return icon;
}

export function Dashboard({ user, location, locations, selectedLocations, onSwitchLocation, onBack }) {
  const [tasks, setTasks] = useState([]);
  const [date] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const [showManager, setShowManager] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showTaskImport, setShowTaskImport] = useState(false);
  // E7: welcome tour. Fires automatically on first ever Dashboard mount
  // if the user has no tour_completed_at timestamp. Replay is wired
  // through the Profile page's "Replay welcome tour" button.
  const [showTour, setShowTour] = useState(!user.tour_completed_at);
  const [editTask, setEditTask] = useState(null);
  // CJ feedback 2026-05-09: owners + admins should land directly on
  // their primary view (Birdseye for owners, Daily for everyone else)
  // instead of the intermediate home-landing card.
  // Manager-alert email deep-link: when /admin/applications/:id is the
  // entry path (handled by the SPA-shell route in server.js), land on the
  // Applications tab with that id pre-selected. Captured once at mount
  // and the URL is cleaned up so subsequent tab switches don't get
  // dragged back to it.
  const initialHiringAppId = (() => {
    if (typeof window === 'undefined') return null;
    const m = window.location.pathname.match(/^\/admin\/applications\/(\d+)\/?$/);
    if (!m) return null;
    try { window.history.replaceState(null, '', '/'); } catch (_) { /* ok */ }
    return Number(m[1]);
  })();
  const [pnlTab, setPnlTab] = useState('pnl');
  const [activeTab, setActiveTab] = useState(() => {
    if (initialHiringAppId) return 'hiring';
    if (typeof window !== 'undefined' && window.EmpireRoles?.isOwner?.(user)) return 'home-birdseye';
    return 'home-daily';
  });
  const [timePeriods, setTimePeriods] = useState([]);
  const [users, setUsers] = useState([]);
  const [disputeAlerts, setDisputeAlerts] = useState([]);
  const [mustReadCount, setMustReadCount] = useState(0);  // Board must-read posts I haven't acked → nav badge
  const [showAlerts, setShowAlerts] = useState(false);
  // Round 14: location switcher dropdown in the sidebar
  const [locSwitchOpen, setLocSwitchOpen] = useState(false);

  // Cross-view navigation (e.g. Payroll's "edit in Cash counts" link).
  useEffect(() => {
    const go = (e) => { if (typeof e.detail === 'string') setActiveTab(e.detail); };
    window.addEventListener('monarch:navigate', go);
    return () => window.removeEventListener('monarch:navigate', go);
  }, []);

  // Deep link: /?tab=<navId> — e.g. the Board broadcast email's "Open on the
  // Board" button uses ?tab=board. Land on that tab, then strip the param so a
  // refresh doesn't re-pin it over the user's navigation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (!t) return;
    setActiveTab(t);
    params.delete('tab');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []);

  // Declutter ★4: old financial destinations fold into Reports → P&L tabs.
  // Redirect any stale deep link / saved tab so nothing renders blank.
  useEffect(() => {
    if (activeTab === 'home-financial') { setPnlTab('health'); setActiveTab('reports-pnl'); }
    if (activeTab === 'settings-fixed-costs') { setPnlTab('inputs'); setActiveTab('reports-pnl'); }
    const aliased = resolveTabAlias(activeTab, window.EmpireRoles.isManager(user));
    if (aliased !== activeTab) setActiveTab(aliased);
  }, [activeTab]);

  // Silent re-login for an expired Roast Curve session. When RC's 12h cookie
  // lapses it bounces the tab back here with ?launch=roastcurve; re-fire the
  // existing handoff (same tab) so the roaster lands back in Roast Curve
  // instead of a dead "not signed in" screen. No auth change — this just
  // auto-clicks the launch they'd otherwise click. Guarded: URL is cleaned so
  // a refresh can't re-fire, and a failed re-mint quietly stays on the dashboard.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('launch') !== 'roastcurve') return;
    params.delete('launch');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    if (!user?.id) return;
    api('POST', '/api/roastcurve/handoff', {}, { 'x-user-id': String(user.id) })
      .then((r) => { if (r?.url) window.location.assign(r.url); })
      .catch(() => { /* stay on the dashboard; the 🔥 Roast Curve tab still works */ });
  }, []);
  // Round 32: mobile location picker — sidebar is hidden below 767px so
  // the location subtitle in the page header opens a small picker instead.
  const [mobileLocPickerOpen, setMobileLocPickerOpen] = useState(false);

  // Round 8: clicking a row in Team opens a profile. activeTab flips to
  // 'profile' and selectedProfileId carries the target user id.
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  function openProfile(targetUserId) {
    setSelectedProfileId(targetUserId);
    setActiveTab('profile');
  }
  function closeProfile() {
    setSelectedProfileId(null);
    setActiveTab('team');
  }

  const isManager = window.EmpireRoles.isManager(user);
  const hasCapability = (cap) => window.EmpireRoles.hasCapability(user, cap);
  // Shift Leads (and managers/owners) get the Reports → Cash counts surface.
  const canEnterCashCount = hasCapability('enter_cash_count');
  // Shift Leads (and managers/owners) get the Task editor; bulk import /
  // bulk delete stay manager-only under manage_tasks.
  const canEditTasks = hasCapability('edit_tasks');
  const canManageTasks = hasCapability('manage_tasks');
  // Scoped editors (Shift Leads, no view_all_locations) only get their
  // assigned stores in the task modal's location picker — the server
  // enforces the same boundary with a 403.
  const taskEditorLocations = (() => {
    if (hasCapability('view_all_locations')) return locations;
    let ids = [];
    if (user.location_ids) { try { ids = JSON.parse(user.location_ids); } catch {} }
    else if (user.location_id) ids = [user.location_id];
    return locations.filter(l => ids.includes(l.id));
  })();

  async function loadTimePeriods() {
    const data = await api('GET', '/api/time-periods');
    setTimePeriods(data);
  }

  useEffect(() => {
    loadTimePeriods();
    api('GET', '/api/users').then(setUsers);
    api('GET', `/api/disputes/my-alerts?user_id=${user.id}`)
      .then(setDisputeAlerts)
      .catch(() => {});
    // Board must-read badge: how many require-ack posts I haven't acknowledged.
    // Poll gently (60s) like the notification bell so the badge clears/appears
    // without a full reload.
    const loadMustRead = () => api('GET', '/api/board/must-read-count', null, { 'x-user-id': String(user.id) })
      .then((r) => setMustReadCount(r?.count || 0)).catch(() => {});
    loadMustRead();
    const mustReadTimer = setInterval(loadMustRead, 60000);
    // Cross-component navigation hook (e.g. SendMessageButton in
    // EmployeeProfileView wants to jump to the Chat tab).
    function onNav(e) {
      const next = e.detail?.tab;
      if (next) setActiveTab(next);
    }
    window.addEventListener('monarch-nav', onNav);

    // R44 staff routing: on first mount, if you're a staffer (not
    // manager+), look up today's shift. If you're scheduled, switch
    // the active location to that shift's café and land on Home →
    // Daily so the clock-in card is right there. If you have no
    // shift today, drop you at Schedule so you can see the week.
    if (!isManager) {
      api('GET', '/api/me/shifts/upcoming', null, { 'x-user-id': String(user.id) }).then((r) => {
        const todayYmd = todayStr();
        const shifts = r.shifts || [];
        const todayShift = shifts.find(s => s.shift_date === todayYmd);
        // Employees default to the location of their next-scheduled
        // shift — today's if there is one, otherwise the soonest
        // future shift, otherwise no override (sticks with the
        // assigned/primary that App.js picked).
        const next = todayShift || shifts[0];
        if (next?.location_id && next.location_id !== location?.id) {
          const target = (locations || []).find(l => l.id === next.location_id);
          if (target && onSwitchLocation) onSwitchLocation(target);
        }
        // Land on Home → Daily if scheduled today, Schedule if not.
        setActiveTab(todayShift ? 'home-daily' : 'sched-mine');
      }).catch(() => {});
    }

    return () => { window.removeEventListener('monarch-nav', onNav); clearInterval(mustReadTimer); };
  }, []);

  // Never raise `loading` here: the placeholder collapses the page and resets scroll.
  async function loadTasks() {
    const data = await api('GET', `/api/tasks/${location.id}?date=${date}`);
    setTasks(data);
    setLoading(false);
  }

  useEffect(() => { setLoading(true); loadTasks(); }, [location.id, date]);

  async function handleComplete(taskId, measurement) {
    const body = { task_id: taskId, location_id: location.id, user_id: user.id, date, notes: null };
    if (measurement != null) body.measurement = measurement;
    try {
      await api('POST', '/api/complete', body);
    } catch (e) {
      // api() throws Error(responseText) on non-2xx. The 403 from
      // /api/complete is a JSON body { error: 'Clock in before…' };
      // surface that verbatim so the staffer knows what to fix.
      let msg = e?.message || 'Could not complete the task.';
      try { const parsed = JSON.parse(String(msg)); if (parsed?.error) msg = parsed.error; } catch (_) {}
      alert(msg);
      return;
    }
    await loadTasks();
  }

  async function handleDelete(taskId) {
    if (!confirm('Delete this task?')) return;
    await api('DELETE', `/api/tasks/${taskId}`);
    loadTasks();
  }

  // 'Anytime' is a virtual period at sort_order 0 (top of the list) for
  // tasks that aren't tied to a specific shift window — they show up
  // every day, completable by anyone. The matcher accepts blank /
  // 'Anytime' / 'Any' so legacy rows still bucket cleanly.
  const activePeriods = [
    { id: 0, name: 'Anytime', sort_order: 0, isAnytime: true },
    ...(timePeriods.length > 0 ? timePeriods : [
      {id:0,name:'Opening',sort_order:1},{id:0,name:'Morning',sort_order:2},
      {id:0,name:'Midday',sort_order:3},{id:0,name:'Afternoon',sort_order:4},{id:0,name:'Closing',sort_order:5}
    ]),
  ];
  function periodMatches(t, name) {
    const tp = (t.effective_period || t.time_period || '').trim();
    if (name === 'Anytime') return !tp || /^(anytime|any|all\s*day)$/i.test(tp);
    return tp.toLowerCase() === name.toLowerCase();
  }
  // Weekly day-of-week filter for the Daily view. Tasks with
  // recurrence='weekly' and a non-empty recurrence_days array only appear
  // on the days they were scheduled for. Logic lives in
  // shared/task-recurrence.js so the iPad kiosk can apply the same filter
  // without duplication. The unfiltered `tasks` array still passes to
  // AdminView below so the Task editor can manage tasks scheduled for
  // any day.
  const visibleTasks = window.TaskRecurrence.filterByDay(tasks, date);
  const byPeriod = activePeriods.reduce((acc, p) => {
    acc[p.name] = visibleTasks.filter(t => periodMatches(t, p.name));
    return acc;
  }, {});
  const completed = visibleTasks.filter(t => t.status === 'completed').length;
  const total = visibleTasks.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const isOwner = window.EmpireRoles.isOwner(user);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // Phase 1.2 — 5-item sidebar IA. Each top-level group has an `icon`
  // and (optionally) sub-items whose `id` is the existing `activeTab`
  // value so the view-routing below stays untouched. Schedule / Info /
  // Recipes are placeholders that ship in later phases. Settings is
  // pinned to the bottom of the sidebar.
  // IA per CJ 2026-04-28:
  // - Home top-level → Reports sub-item (Daily/This Week/Year inner tabs)
  // - Performance moved out of Home and into Tasks → Performance
  // - Tasks gets the moved Performance + the existing operational pages
  // - Team is a TOP-LEVEL item (applies cross-feature, not nested under Settings)
  // - Settings shrinks; its only sub-item today is Settings → Tasks (Disputes,
  //   Dispute History, Suggestions, Time Periods). Schedule settings will join
  //   later.
  // Round 30: Dashboard removed from sidebar — Availability Health lives
  // inside Scheduling → Availability Health. Home remains the default
  // landing tab.
  // R40 nav restructure: top-level groups bin pages by workflow. The
  // sidebar shows ONLY top-level entries; sub-items render in the
  // universal .mobile-subnav strip at the top of each page.
  //
  // De-duplicated: Equipment / Maintenance / Compliance previously lived
  // in BOTH Tasks and Knowledge Hub. They now have a single home in
  // Operations.
  const navGroups = buildNavGroups({
    user, hasCapability, isOwner, isManager,
    canEditTasks, canEnterCashCount,
  });
  const settingsGroup = null;

  // Resolve the readable label for the current view (used in the page
  // header). Walk the new nav tree first, then fall back to the leaf id.
  function resolveActiveLabel(tab) {
    if (tab === 'home') return 'Home';
    if (tab === 'admin') return 'Settings';
    if (tab === 'schedule') return 'Schedule';
    if (tab === 'info') return 'Info';
    if (tab === 'team') return 'Team';
    const allGroups = settingsGroup ? [...navGroups, settingsGroup] : navGroups;
    for (const g of allGroups) {
      const hit = (g.children || []).find((c) => c.id === tab);
      if (hit) return hit.label;
    }
    return '';
  }
  const activeLabel = resolveActiveLabel(activeTab);

  // Mobile bottom-tabs are capped at 5: Home, Tasks, Schedule, Chat,
  // More. Anything not in that set lives under "More" (a bottom-sheet
  // drawer). Owners + managers get a longer top-nav; we don't push
  // that count to the bottom bar because 5 is the iOS-standard ceiling.
  const PRIMARY_MOBILE_IDS = ['home', 'tasks', 'schedule'];   // 'chat' deprecated 2026-07-29
  const moreGroups = navGroups.filter((g) => !PRIMARY_MOBILE_IDS.includes(g.id));
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const mobileBottomTabs = [
    ...PRIMARY_MOBILE_IDS.map((id) => navGroups.find((g) => g.id === id)).filter(Boolean).map((g) => ({
      id: g.id, label: g.label, icon: g.icon,
      firstChildId: (g.children && g.children.length) ? g.children[0].id : g.id,
    })),
    { id: '_more', label: 'More', icon: '☰', isMore: true },
  ];

  // Clicking a parent in the desktop sidebar jumps to its first child
  // when there is one — keeps the click target useful instead of
  // landing on a dead "Schedule" parent panel.
  // Open Tally straight from the menu — no intermediate page. The tab is
  // opened synchronously within the click (so popup blockers don't eat it),
  // then redirected to the signed 60s handoff URL once it resolves. Popup
  // blocked → same-tab nav; handoff fails → land on the launch card so the
  // error shows with a retry.
  function openTally() {
    let w = null;
    try { w = window.open('', '_blank'); } catch { w = null; }
    if (w) { try { w.document.write('<title>Opening Tally…</title><p style="font:16px system-ui,sans-serif;color:#555;padding:2rem">Opening Tally…</p>'); } catch { /* ok */ } }
    api('POST', '/api/tally/handoff', {}, { 'x-user-id': String(user.id) })
      .then((r) => {
        if (w && !w.closed) w.location = r.url;
        else window.location.assign(r.url);
      })
      .catch(() => {
        if (w && !w.closed) w.close();
        setActiveTab('tally');
      });
  }

  // Roast Curve: identical launch shape (synchronous window.open so popup
  // blockers don't eat it; failed handoff lands on the launch card).
  function openRoastCurve() {
    let w = null;
    try { w = window.open('', '_blank'); } catch { w = null; }
    if (w) { try { w.document.write('<title>Opening Roast Curve…</title><p style="font:16px system-ui,sans-serif;color:#555;padding:2rem">Opening Roast Curve…</p>'); } catch { /* ok */ } }
    api('POST', '/api/roastcurve/handoff', {}, { 'x-user-id': String(user.id) })
      .then((r) => {
        if (w && !w.closed) w.location = r.url;
        else window.location.assign(r.url);
      })
      .catch(() => {
        if (w && !w.closed) w.close();
        setActiveTab('roastcurve');
      });
  }

  function handleNavClick(group) {
    if (group.id === 'tally') { openTally(); return; }
    if (group.id === 'roastcurve') { openRoastCurve(); return; }
    if (group.children && group.children.length) {
      setActiveTab(group.children[0].id);
    } else {
      setActiveTab(group.id);
    }
  }
  function isGroupActive(group) {
    if (activeTab === group.id) return true;
    return (group.children || []).some((c) => c.id === activeTab);
  }

  return (
    <>
      {showAlerts && (
        <DisputeAlertsModal user={user} alerts={disputeAlerts}
          onDismiss={id => setDisputeAlerts(a => a.filter(x => x.id !== id))}
          onClose={() => setShowAlerts(false)} />
      )}

      <div className="app-layout">
        {/* ── Sidebar ── */}
        <aside className="sidebar">
          <div className="sidebar-brand" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="assets/monarch-logo.png" alt=""
              style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0,
                        filter: 'brightness(0) invert(1) opacity(0.85)' }} />
            <div style={{ overflow: 'hidden' }}>
              <div className="sidebar-brand-sub">Empire Tea & Coffee</div>
              <div className="sidebar-brand-name">Monarch</div>
              <div className="sidebar-brand-tag">Operations</div>
            </div>
          </div>

          {/* Round 14: location switcher pinned at the top of the sidebar
              (replaces the page-header pill bar that was duplicated on
              every screen). Single-location users see a static label. */}
          {(() => {
            const switchable = (() => {
              const role = (user.role || '').toLowerCase();
              const canSeeAll = window.EmpireRoles.hasCapability(user, 'view_all_locations');
              if (canSeeAll && Array.isArray(locations) && locations.length > 1) return locations;
              if (selectedLocations && selectedLocations.length > 1) return selectedLocations;
              return null;
            })();
            return (
              <div className="sidebar-locswitch" onMouseLeave={() => setLocSwitchOpen(false)}>
                <button className="sidebar-locswitch-btn"
                  onClick={() => switchable && setLocSwitchOpen((s) => !s)}
                  title={switchable ? 'Switch location' : location.name}>
                  <span className="sidebar-locswitch-icon">⌖</span>
                  <span className="sidebar-label" style={{ flex: 1, textAlign: 'left' }}>
                    {location.name}
                  </span>
                  {switchable && <span className="sidebar-label sidebar-locswitch-caret">▼</span>}
                </button>
                {switchable && locSwitchOpen && (
                  <div className="sidebar-locswitch-menu">
                    {switchable.map((l) => (
                      <button key={l.id}
                        className={`sidebar-locswitch-option${l.id === location.id ? ' active' : ''}`}
                        onClick={() => { onSwitchLocation(l); setLocSwitchOpen(false); }}>
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <nav className="sidebar-nav">
            {navGroups.map((group) => (
              <div key={group.id} className="nav-group">
                <button
                  data-tour-id={group.id}
                  className={`nav-item${isGroupActive(group) ? ' active' : ''}`}
                  onClick={() => handleNavClick(group)}>
                  <span className="nav-icon">{navIcon(group.icon)}</span>
                  <span className="sidebar-label">{group.label}</span>
                  {group.id === 'board' && mustReadCount > 0 && (
                    <span className="sidebar-label" title={`${mustReadCount} must-read ${mustReadCount === 1 ? 'post' : 'posts'} to acknowledge`}
                      style={{ marginLeft: 'auto', background: '#c62828', color: '#fff', fontSize: 10, fontWeight: 700,
                                borderRadius: 999, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>
                      {mustReadCount > 9 ? '9+' : mustReadCount}
                    </span>
                  )}
                </button>
                {group.children && group.children.length > 0 && (
                  <div className="nav-children sidebar-label">
                    {group.children.map((child) => (
                      child.external ? (
                        <a key={child.id} href={child.external}
                           target="_blank" rel="noopener noreferrer"
                           className="nav-child">
                          {child.label}
                        </a>
                      ) : (
                        <button key={child.id}
                          className={`nav-child${activeTab === child.id ? ' active' : ''}`}
                          onClick={() => setActiveTab(child.id)}>
                          {child.label}
                        </button>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {settingsGroup && (
            <nav className="sidebar-nav-settings">
              <div className="nav-group">
                <button
                  className={`nav-item${isGroupActive(settingsGroup) && !settingsGroup.children ? ' active' : ''}`}
                  onClick={() => handleNavClick(settingsGroup)}>
                  <span className="nav-icon">{navIcon(settingsGroup.icon)}</span>
                  <span className="sidebar-label">{settingsGroup.label}</span>
                </button>
                {settingsGroup.children && settingsGroup.children.length > 0 && (
                  <div className="nav-children sidebar-label">
                    {settingsGroup.children.map((child) => (
                      <button key={child.id}
                        className={`nav-child${activeTab === child.id ? ' active' : ''}`}
                        onClick={() => setActiveTab(child.id)}>
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          )}

          <div className="sidebar-footer">
            {/* Round 15: clicking the user identity opens the staff's
                own profile (shortcut to Settings → Team → click row). */}
            <div className="sidebar-user"
              onClick={() => openProfile(user.id)}
              title="Open my profile"
              style={{ cursor: 'pointer' }}>
              <div className="user-avatar">{user.name ? user.name[0].toUpperCase() : '?'}</div>
              <div className="sidebar-user-info">
                <div style={{color:'#fff',fontSize:'13px',fontWeight:600}}>{user.name}</div>
                <div style={{color:'rgba(255,255,255,0.45)',fontSize:'11px'}}>{user.role}</div>
              </div>
            </div>
            <button className="sidebar-logout" onClick={onBack} title="Sign out">
              <span className="sidebar-logout-icon">⏻</span>
              <span className="sidebar-label">Sign Out</span>
            </button>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <div className="main-content">
          <div className="main-header">
            <div className="main-header-inner">
              <div>
                {activeTab === 'today' ? (
                  <>
                    <div className="greeting">{greeting}, {user.name ? user.name.split(' ')[0] : ''}!</div>
                    <div className="header-sub" style={{ position: 'relative' }}>
                      {selectedLocations && selectedLocations.length > 1 ? (
                        <button onClick={() => setMobileLocPickerOpen((v) => !v)}
                          className="header-loc-switcher"
                          style={{ background: 'transparent', border: 'none', padding: 0,
                                    color: 'inherit', font: 'inherit', cursor: 'pointer',
                                    textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                          {location.name}
                          <span className="header-loc-switcher-caret"> ▾</span>
                        </button>
                      ) : (
                        <span>{location.name}</span>
                      )}
                      {' · '}{today}
                      {mobileLocPickerOpen && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6,
                                       background: '#fff', border: '1px solid var(--rule)',
                                       borderRadius: 8, boxShadow: '0 8px 24px rgba(31,42,68,0.15)',
                                       minWidth: 180, zIndex: 50, overflow: 'hidden' }}>
                          {selectedLocations.map((l) => (
                            <button key={l.id}
                              onClick={() => { onSwitchLocation(l); setMobileLocPickerOpen(false); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left',
                                        padding: '10px 14px', background: l.id === location.id ? 'var(--cream-soft)' : '#fff',
                                        border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                                        fontWeight: l.id === location.id ? 700 : 500,
                                        color: 'var(--text-primary)' }}>
                              {l.id === location.id ? '● ' : '○ '}{l.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="page-title">{activeLabel}</div>
                    <div className="header-sub" style={{ position: 'relative' }}>
                      {selectedLocations && selectedLocations.length > 1 ? (
                        <button onClick={() => setMobileLocPickerOpen((v) => !v)}
                          className="header-loc-switcher"
                          style={{ background: 'transparent', border: 'none', padding: 0,
                                    color: 'inherit', font: 'inherit', cursor: 'pointer',
                                    textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                          {location.name}
                          <span className="header-loc-switcher-caret"> ▾</span>
                        </button>
                      ) : (
                        <span>{location.name}</span>
                      )}
                      {' · '}{today}
                      {mobileLocPickerOpen && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6,
                                       background: '#fff', border: '1px solid var(--rule)',
                                       borderRadius: 8, boxShadow: '0 8px 24px rgba(31,42,68,0.15)',
                                       minWidth: 180, zIndex: 50, overflow: 'hidden' }}>
                          {selectedLocations.map((l) => (
                            <button key={l.id}
                              onClick={() => { onSwitchLocation(l); setMobileLocPickerOpen(false); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left',
                                        padding: '10px 14px', background: l.id === location.id ? 'var(--cream-soft)' : '#fff',
                                        border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                                        fontWeight: l.id === location.id ? 700 : 500,
                                        color: 'var(--text-primary)' }}>
                              {l.id === location.id ? '● ' : '○ '}{l.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="header-actions">
                {/* Profile shortcut — primary entry to own profile on mobile,
                    where the sidebar avatar isn't rendered. Also useful on
                    desktop as a faster path than clicking the sidebar avatar. */}
                <button onClick={() => openProfile(user.id)}
                  title="Open my profile"
                  aria-label="Open my profile"
                  style={{ width: 36, height: 36, borderRadius: '50%',
                            background: 'var(--forest)', color: '#fff',
                            border: 'none', cursor: 'pointer',
                            fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0 }}>
                  {(user.name || '?').slice(0, 1).toUpperCase()}
                </button>
                <NotificationBell currentUser={user} onOpenLink={(tab) => setActiveTab(tab)} />
                {activeTab === 'today' && !isManager && (
                  <button className="btn-gold" onClick={() => setShowSuggest(true)}>
                    💡 Suggest
                  </button>
                )}
              </div>
            </div>
            {activeTab === 'today' && (
              <div className="header-progress">
                <div className="header-progress-bar">
                  <div className="header-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="header-progress-label">{completed}/{total} · {pct}%</span>
              </div>
            )}
            {/* Round 16: page-header location pills removed entirely. The
                sidebar location dropdown is the only switcher now. */}
          </div>

          {/* Mobile sub-tab strip — children of the active top-level group.
              Hidden on desktop where the sidebar handles this. */}
          {(() => {
            const activeGroup = navGroups.find(
              (g) => g.id === activeTab || (g.children || []).some((c) => c.id === activeTab)
            );
            const children = activeGroup?.children || [];
            if (children.length < 2) return null;
            return (
              <div className="mobile-subnav">
                {children.map((c) => {
                  const cls = `mobile-subnav-item${activeTab === c.id ? ' active' : ''}`;
                  return c.external ? (
                    <a key={c.id} href={c.external} target="_blank" rel="noopener noreferrer"
                       className={cls}>
                      {c.label}
                    </a>
                  ) : (
                    <button key={c.id} className={cls}
                      onClick={() => setActiveTab(c.id)}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          <div className="page-content">
            {disputeAlerts.length > 0 && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => setShowAlerts(true)}>
                <div className="flex items-center gap-3">
                  <span className="text-amber-500 text-lg flex-shrink-0">⚠</span>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      You have {disputeAlerts.length} task review{disputeAlerts.length > 1 ? 's' : ''} to address
                    </p>
                    <p className="text-xs text-amber-600">Someone filed an issue against your completed work. Tap to review.</p>
                  </div>
                </div>
                <span className="text-amber-400 text-lg flex-shrink-0 ml-3">→</span>
              </div>
            )}

            {/* CJ 2026-07-29: count-money + waste/shift-meals now sit at the TOP
                of the Tasks page (web/phone app), not as list items and no longer
                on Daily. Kiosk cash/inventory screens are unchanged. */}
            {activeTab === 'today' && (
              <div className="space-y-3 mb-4">
                <DailyCashCountEntry location={location} user={user} />
                <InventoryLogCard location={location} user={user} />
              </div>
            )}

            {activeTab === 'today' && (
              loading ? (
                <div className="flex items-center justify-center h-40"><div className="text-gray-400">Loading tasks...</div></div>
              ) : (
                <div className="space-y-8">
                  {activePeriods.map((periodObj, pidx) => {
                    const period = periodObj.name;
                    const allPeriodTasks = byPeriod[period] || [];
                    if (!allPeriodTasks.length) return null;
                    const done = allPeriodTasks.filter(t => t.status === 'completed').length;
                    const pendingTasks = allPeriodTasks.filter(t => t.status !== 'completed');
                    const completedTasks = allPeriodTasks.filter(t => t.status === 'completed');
                    const timeLabel = (periodObj.start_time && periodObj.end_time)
                      ? `${periodObj.start_time}–${periodObj.end_time}`
                      : (PERIOD_TIMES[period] || '');
                    return (
                      <div key={period}>
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`period-badge px-2 py-1 rounded-md ${periodColor(period, pidx)}`}>{period}</span>
                          <span className="text-xs text-gray-400">{timeLabel}</span>
                          <span className="ml-auto text-xs text-gray-400 font-medium">{done}/{allPeriodTasks.length}</span>
                        </div>
                        {pendingTasks.length > 0 ? (
                          <div className="lg:grid lg:grid-cols-2 lg:gap-3 space-y-2 lg:space-y-0">
                            {pendingTasks.map(task => (
                              <TaskCard key={task.id} task={task} onComplete={handleComplete}
                                userId={user.id} locationId={location.id} date={date}
                                isManager={isManager} onFlagSubmit={loadTasks}
                                onEdit={t => { setEditTask(t); setShowManager(true); }} />
                            ))}
                          </div>
                        ) : (
                          <div className="py-3 px-4 bg-green-50 border border-green-200 rounded-xl text-center">
                            <p className="text-green-700 text-sm font-semibold">✓ All {period} tasks complete</p>
                          </div>
                        )}
                        {/* Completed tasks have moved to Tasks → Recent
                            activity (date-paginated, prev/today/next). */}
                      </div>
                    );
                  })}
                  {visibleTasks.length === 0 && (
                    <div className="text-center py-16 text-gray-400">
                      <p className="text-4xl mb-3">📋</p>
                      <p className="font-medium">No tasks for today</p>
                      {canEditTasks && <p className="text-sm mt-1">Use the Task editor (Tasks → Task editor) to create tasks</p>}
                    </div>
                  )}
                </div>
              )
            )}

            {activeTab === 'yesterday' && (
              <YesterdayView locationId={location.id} userId={user.id} isManager={isManager} timePeriods={timePeriods} />
            )}

            {/* CJ 2026-05-21: Equipment, Temperatures, Maintenance are now
                visible to staff (read-only inside the components).
                Compliance stays manager+. */}
            {activeTab === 'maintenance' && (
              <MaintenanceView locationId={location.id} isManager={isManager} />
            )}

            {activeTab === 'equipment' && (
              <EquipmentView location={location} user={user} />
            )}

            {activeTab === 'ops-temperatures' && (
              <TemperaturesOpsView user={user} />
            )}

            {activeTab === 'compliance' && isManager && (
              <ComplianceView user={user} location={location} locations={locations} />
            )}

            {/* Settings → Tasks (sub-item) — Suggestions / Time Periods
                panels per CJ 2026-04-28. */}
            {activeTab === 'settings-tasks' && canEditTasks && (
              <AdminView locations={locations} location={location} tasks={tasks}
                onEditTask={t => { setEditTask(t); setShowManager(true); }}
                onDeleteTask={handleDelete}
                onAddTask={() => { setEditTask(null); setShowManager(true); }}
                onImportTasks={canManageTasks ? () => setShowTaskImport(true) : undefined}
                onRefresh={loadTasks}
                currentUser={user}
                timePeriods={timePeriods} onTimePeriodsChange={loadTimePeriods}
                panels={['tasks', 'suggestions', 'periods']} />
            )}
            {/* One Disputes surface (systems review 2026-07-22): open queue +
                history together — previously two nav entries for one job. The
                old tasks-dispute-history id keeps working as an alias. */}
            {(activeTab === 'tasks-disputes' || activeTab === 'tasks-dispute-history') && isManager && (
              <AdminView locations={locations} location={location} tasks={tasks}
                onEditTask={t => { setEditTask(t); setShowManager(true); }}
                onDeleteTask={handleDelete} onRefresh={loadTasks}
                currentUser={user}
                timePeriods={timePeriods} onTimePeriodsChange={loadTimePeriods}
                panels={['flags', 'history']} />
            )}
            {activeTab === 'settings-locations' && isManager && (
              <LocationConfigView locations={locations} currentUser={user} location={location} />
            )}
            {activeTab === 'settings-audit' && isManager && (
              <OverrideAuditView currentUser={user} />
            )}
            {activeTab === 'settings-admin-toggles' && isOwner && (
              <AdminTogglesView currentUser={user} />
            )}
            {activeTab === 'settings-holidays' && (
              <HolidayListView currentUser={user} readOnly={!isOwner} />
            )}
            {activeTab === 'settings-payroll-roster' && isOwner && (
              <PayrollRosterView currentUser={user} />
            )}
            {activeTab === 'settings-hiring' && isManager && (
              <HiringSettingsView currentUser={user} locations={locations} />
            )}
            {activeTab === 'settings-events' && isManager && (
              <EventsSettingsView currentUser={user} locations={locations} />
            )}
            {activeTab === 'reports-pnl' && isOwner && (
              <div>
                {/* Declutter ★4: one deep money view. P&L is the report;
                    Financial health and the fixed-cost/lease inputs are its
                    companion tabs instead of separate nav destinations. */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {[['pnl', 'P&L'], ['health', 'Financial health'], ['inputs', 'Cost inputs']].map(([k, l]) => (
                    <button key={k} onClick={() => setPnlTab(k)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${pnlTab === k ? 'bg-crimson text-white border-crimson' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {pnlTab === 'pnl' && <PnlReportView currentUser={user} />}
                {pnlTab === 'health' && <FinancialHealthView currentUser={user} />}
                {pnlTab === 'inputs' && (
                  <div className="space-y-4">
                    <FixedCostsView currentUser={user} locations={locations} />
                    <LeaseDatesView currentUser={user} locations={locations} />
                  </div>
                )}
              </div>
            )}
            <TabMounts activeTab={activeTab} user={user} locations={locations}
              isOwner={isOwner} isManager={isManager} canEnterCashCount={canEnterCashCount} />

            {/* Team (Settings → Team in Round 7) — visible to everyone for
                shift-coverage contact. Non-managers see name + role + email
                + locations only. PTO admin and full edit move into the
                profile in Round 8; this view is now read-only directory. */}
            {activeTab === 'team' && (
              <TeamDirectoryView user={user} locations={locations} onOpenProfile={openProfile} />
            )}
            {activeTab === 'tally' && hasCapability('use_tally') && (
              <TallyLaunchView user={user} />
            )}
            {activeTab === 'roastcurve' && hasCapability('use_roastcurve') && (
              <RoastCurveLaunchView user={user} />
            )}
            {activeTab === 'hiring' && (
              <HiringApplicationsView currentUser={user} locations={locations}
                initialAppId={initialHiringAppId} />
            )}
            {activeTab === 'profile' && selectedProfileId && (
              <EmployeeProfileView
                currentUser={user}
                profileId={selectedProfileId}
                locations={locations}
                onClose={closeProfile}
              />
            )}

            {/* Home — landing screen + Reports sub-item. Reports is the
                ops-dashboards (Daily / This Week / Year). Performance
                moved out of Home to Tasks → Performance per CJ. */}
            {activeTab === 'home' && (
              <HomeLanding user={user} location={location}
                completed={completed} total={total} pct={pct} today={today}
                onOpenReports={() => setActiveTab(window.EmpireRoles.isOwner(user) ? 'home-birdseye' : 'home-daily')} />
            )}
            {/* Each Home sub-tab now routes to its own component instead of
                a wrapper view with internal tabs. Daily / This Week / Year
                were previously the inner tabs of HomeReportsView. */}
            {activeTab === 'home-birdseye' && window.EmpireRoles.isOwner(user) && (
              <>
                {/* CJ 2026-07-12: owners land on Birdseye, not the Home
                    landing, so the drip card lives here too — one ack on
                    either surface counts once; self-hides when off/acked. */}
                <div className="mb-3"><KnowledgeDripCard mode="home" user={user} /></div>
                <OwnerDashboard user={user} locations={locations} />
              </>
            )}
            {activeTab === 'home-daily' && (
              <>
                {/* R44: Clock-in card surfaces above Daily for staff
                    (the banner self-hides when there's no actionable
                    state; managers see it too — handy at the café). */}
                {/* CJ 2026-07-29: clock-in retired — Square is the time clock. */}
                {timeclockEnabled() && <div className="mb-3"><PunchBanner user={user} /></div>}
                {/* CJ 2026-07-29: Daily order is Events → Weather → Morning PAR →
                    Today's staffing → Hourly forecast, then the sales scorecard +
                    roster + drip card below. Weather + Morning PAR ride in the
                    realtime block's leadSlot (rendered right after Events, before
                    staffing); cash-count + waste/meals moved to the Tasks page. */}
                <RealtimeDashboard location={location} user={user} variant="daily"
                  leadSlot={<>
                    <TodayWeather location={location} user={user} />
                    <MorningParPanel location={location} user={user} />
                  </>} />
                <div className="mt-3"><KnowledgeDripCard mode="home" user={user} /></div>
              </>
            )}
            {activeTab === 'home-this-week' && (
              <Step3View location={location} user={user} />
            )}
            {activeTab === 'home-year' && (
              <Step1View location={location} user={user} />
            )}
            {/* Legacy notification-link aliases (not nav items); the rest
                are redirected in lib/tab-aliases.js. */}
            {activeTab === 'home-reports' && (
              <RealtimeDashboard location={location} user={user} />
            )}
            {activeTab === 'owner-dashboard' && window.EmpireRoles.isOwner(user) && (
              <OwnerDashboard user={user} locations={locations} />
            )}

            {activeTab === 'kh-recipes' && (
              <RecipeCardsView user={user} />
            )}
            {activeTab === 'kh-tools' && (
              <ColdBrewDilutionView />
            )}
            {activeTab === 'kh-library' && (
              <KbLibraryView user={user} isManager={isManager} />
            )}
            {activeTab === 'kh-ask' && hasCapability('use_ask_empire') && (
              <AskEmpireView user={user} />
            )}
            {activeTab === 'kh-gaps' && isManager && (
              <KbGapReportView user={user} />
            )}

            {/* Tasks → Missed tasks (the surviving slice of the old
                5-section Performance dashboard — declutter ★5). */}
            {activeTab === 'tasks-performance' && isManager && (
              <MissedTasksView locationId={location.id} locationName={location.name} />
            )}

            {/* Schedule top-level + sub-tabs. My Schedule is the default;
                view-toggle (My/Grid/Gantt) lives inside that surface.
                My Time Card is the renamed Timesheets with manager-only
                "all staff" toggle. Shift swaps still a Phase-2 stub. */}
            {(activeTab === 'schedule' || activeTab === 'sched-mine' ||
              activeTab === 'sched-timeoff' ||
              activeTab === 'sched-swaps' || activeTab === 'sched-time' ||
              activeTab === 'sched-build' || activeTab === 'sched-ops') && (
              <ScheduleSection activeTab={activeTab} setActiveTab={setActiveTab}
                location={location} locations={locations} user={user} />
            )}

          </div>
        </div>

        {/* ── Bottom Tabs (mobile) — 5 entries: Home, Tasks, Schedule,
            Chat, More. Anything else lives under "More". ── */}
        <div className="bottom-tabs">
          {mobileBottomTabs.map(item => {
            if (item.isMore) {
              // Active when the current activeTab belongs to one of the
              // groups inside the More drawer.
              const isActive = moreGroups.some((g) =>
                activeTab === g.id || (g.children || []).some((c) => c.id === activeTab));
              return (
                <button key={item.id} className={`bottom-tab${isActive ? ' active' : ''}`}
                  style={{ position: 'relative' }}
                  onClick={() => setShowMoreMenu(true)}>
                  <span className="bottom-tab-icon">{navIcon(item.icon)}</span>
                  <span>{item.label}</span>
                  {mustReadCount > 0 && (
                    <span title={`${mustReadCount} must-read to acknowledge on the Board`}
                      style={{ position: 'absolute', top: 2, right: '50%', marginRight: -18,
                                background: '#c62828', color: '#fff', fontSize: 9, fontWeight: 700,
                                borderRadius: 999, padding: '1px 5px', minWidth: 15, textAlign: 'center' }}>
                      {mustReadCount > 9 ? '9+' : mustReadCount}
                    </span>
                  )}
                </button>
              );
            }
            const group = navGroups.find((g) => g.id === item.id);
            const isActive = activeTab === item.id
              || (group?.children || []).some((c) => c.id === activeTab);
            return (
              <button key={item.id} className={`bottom-tab${isActive ? ' active' : ''}`}
                onClick={() => item.id === 'tally' ? openTally() : item.id === 'roastcurve' ? openRoastCurve() : setActiveTab(item.firstChildId)}>
                <span className="bottom-tab-icon">{navIcon(item.icon)}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* More drawer — bottom sheet listing nav groups not in the
            primary bottom tabs. */}
        {showMoreMenu && (
          <div onClick={() => setShowMoreMenu(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                      zIndex: 60, paddingBottom: 'calc(60px + env(safe-area-inset-bottom))' }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', width: '100%', maxWidth: 480,
                        borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f0f0f0',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>More</div>
                <button onClick={() => setShowMoreMenu(false)}
                  style={{ background: 'transparent', border: 'none', fontSize: 22,
                            color: '#9e9e9e', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              {moreGroups.map((g) => {
                const firstChild = (g.children && g.children.length) ? g.children[0].id : g.id;
                const isActive = activeTab === g.id
                  || (g.children || []).some((c) => c.id === activeTab);
                return (
                  <button key={g.id}
                    onClick={() => { setShowMoreMenu(false); if (g.id === 'tally') openTally(); else if (g.id === 'roastcurve') openRoastCurve(); else setActiveTab(firstChild); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                              padding: '14px 18px', background: isActive ? '#eef2f6' : '#fff',
                              border: 'none', borderBottom: '1px solid #f5f5f5',
                              cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                    <span style={{ fontSize: 22 }}>{navIcon(g.icon, true)}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#212121' }}>{g.label}</span>
                    {g.id === 'board' && mustReadCount > 0 && (
                      <span style={{ marginLeft: 'auto', background: '#c62828', color: '#fff', fontSize: 11, fontWeight: 700,
                                      borderRadius: 999, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>
                        {mustReadCount > 9 ? '9+' : mustReadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showManager && (
        <TaskManagerModal locations={taskEditorLocations} users={users} timePeriods={timePeriods} editTask={editTask}
          currentUser={user}
          onClose={() => { setShowManager(false); setEditTask(null); }}
          onSave={() => { setShowManager(false); setEditTask(null); loadTasks(); }} />
      )}
      {showSuggest && (
        <SuggestTaskModal user={user} locationId={location.id} timePeriods={timePeriods}
          onClose={() => setShowSuggest(false)}
          onSubmit={() => {}} />
      )}
      {showTaskImport && (
        <TaskBulkImportModal user={user}
          onClose={() => setShowTaskImport(false)}
          onImported={() => { setShowTaskImport(false); loadTasks(); }} />
      )}
      {showTour && (
        <WelcomeTour user={user}
          onClose={() => setShowTour(false)} />
      )}
    </>
  );
}
